import React, { useEffect, useMemo, useState } from 'react';

import { useParams, NavLink, useNavigate } from 'react-router-dom';

import { useCatalogSettings, useTheme } from '../../App';

import BackButton from '../../components/common/BackButton/BackButton';

import { apiClient } from '../../services/apiClient';



function formatCurrency(price) {

  if (typeof price !== 'number' || Number.isNaN(price)) return 'Contact for price';

  return `Rs. ${price.toFixed(2)}`;

}



export default function ProductDetail() {

  const { id } = useParams();

  const navigate = useNavigate();

  const { isNightMode } = useTheme();

  const { showProductRate } = useCatalogSettings();



  const [product, setProduct] = useState(null);

  const [relatedProducts, setRelatedProducts] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState('');

  const [isMobile, setIsMobile] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [enquiryData, setEnquiryData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [enquiryStatus, setEnquiryStatus] = useState('');



  useEffect(() => {

    let cancelled = false;



    const loadProduct = async () => {

      try {

        setIsLoading(true);

        setError('');



        const [productData, relatedData] = await Promise.all([

          apiClient.get(`/products/${encodeURIComponent(id)}`),

          apiClient.get(`/products/${encodeURIComponent(id)}/related?limit=4`),

        ]);



        if (cancelled) return;



        setProduct(productData);

        setRelatedProducts(Array.isArray(relatedData) ? relatedData : []);

      } catch (err) {

        console.error('Failed to load product from backend', err);

        if (!cancelled) {

          setProduct(null);

          setRelatedProducts([]);

          setError(err.message || 'Product not found.');

        }

      } finally {

        if (!cancelled) {

          setIsLoading(false);

        }

      }

    };



    loadProduct();



    return () => {

      cancelled = true;

    };

  }, [id]);



  useEffect(() => {

    if (typeof window === 'undefined') return undefined;



    const handleResize = () => {

      setIsMobile(window.innerWidth <= 768);

    };



    handleResize();

    window.addEventListener('resize', handleResize);



    return () => {

      window.removeEventListener('resize', handleResize);

    };

  }, []);



  const handleSubmitEnquiry = async () => {
    // Validation
    if (!enquiryData.name.trim() || !enquiryData.phone.trim() || !enquiryData.message.trim()) {
      setEnquiryStatus('Please fill in all required fields (Name, Phone, and Message)');
      setTimeout(() => setEnquiryStatus(''), 3000);
      return;
    }

    // Store data before clearing
    const originalEnquiryData = { ...enquiryData };

    // INSTANT SUCCESS - Show success immediately
    setEnquiryData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
    setEnquiryStatus('Enquiry submitted successfully! We will contact you soon.');
    
    // Close modal after 1 second and clear status
    setTimeout(() => {
      setShowEnquiryModal(false);
      setEnquiryStatus('');
    }, 1000);

    // Process in background WITHOUT blocking UI
    (async () => {
      try {
        await apiClient.post('/contact', {
          name: originalEnquiryData.name,
          email: originalEnquiryData.email,
          phone: originalEnquiryData.phone,
          subject: 'Enquiry from Product Page',
          message: originalEnquiryData.message
        });
        // Success - no action needed as we already showed success
      } catch (error) {
        console.error('Background submission failed:', error);
        // If background fails, show error and restore form
        setEnquiryData(originalEnquiryData);
        setEnquiryStatus('Failed to submit enquiry. Please try again or contact us directly.');
        setTimeout(() => setEnquiryStatus(''), 5000);
      }
    })();
  };

  const galleryImages = useMemo(() => {

    if (!product) return [];

    const additionalImages = Array.isArray(product.images) ? product.images : [];
    
    // Remove the main image from additional images to avoid duplication
    const filteredAdditionalImages = additionalImages.filter(img => img !== product.imageUrl);
    
    const merged = [product.imageUrl, ...filteredAdditionalImages]

      .filter(Boolean);

    return merged.length ? merged : [''];

  }, [product]);



  if (isLoading && !product) {

    return (

      <main style={{ padding: 24, fontFamily: 'Arial, sans-serif' }}>

        <p>Loading product...</p>

      </main>

    );

  }



  if (!product) {
    return (
      <main style={{ padding: 24, fontFamily: 'Arial, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <BackButton fallbackTo="/products" />
          <h1 style={{ margin: 0, textAlign: 'center', color: 'red', flex: 1 }}>
            {error || 'Product not found'}
          </h1>
          <div style={{ width: '80px' }}></div>
        </div>
      </main>
    );
  }



  const containerBg = isNightMode ? '#020617' : '#ffffff';

  const cardBg = isNightMode ? '#0f172a' : '#f9fafb';

  const textColor = isNightMode ? '#e5e7eb' : '#111827';

  const mainImage = galleryImages[activeImageIndex] || galleryImages[0];

  const description =

    product.description ||

    'Durable, reliable and designed for modern interiors. Ideal for a wide range of residential and commercial applications.';

  const specifications = [

    product.sku ? `SKU: ${product.sku}` : null,

    product.brand ? `Brand: ${product.brand}` : null,

    product.tag ? `Category: ${product.tag}` : null,

    product.inStock === false ? 'Availability: Out of stock' : 'Availability: In stock',

  ].filter(Boolean);



  return (

    <main

      style={{

        padding: isMobile ? 16 : 24,

        minHeight: '100vh',

        background: containerBg,

        color: textColor,

        fontFamily: 'Arial, sans-serif',

      }}

    >

      <div style={{ maxWidth: 1180, margin: '0 auto' }}>

        <nav

          style={{

            marginBottom: isMobile ? 12 : 16,

            fontSize: isMobile ? 12 : 13,

            display: 'flex',

            alignItems: 'center',

            gap: 12,

            flexWrap: 'wrap',

          }}

          aria-label="Breadcrumb"

        >

          <BackButton fallbackTo="/products" />



          <div>

            <NavLink to="/" style={{ marginRight: 8, textDecoration: 'none', color: '#6b7280' }}> Home
              </NavLink>

            <span aria-hidden>{'>'}</span>

            <NavLink to="/products" style={{ margin: '0 8px', textDecoration: 'none', color: '#6b7280' }}>
                Catalog
              </NavLink>
              
              {product.categoryId?.name ? (
                <>
                  <span aria-hidden>{'>'}</span>
                  <NavLink to={`/products/${product.categoryId.slug || product.categoryId._id}`} style={{ margin: '0 8px', textDecoration: 'none', color: '#6b7280' }}>
                    {product.categoryId.name}
                  </NavLink>
                </>
              ) : product.tag ? (
                <>
                  <span aria-hidden>{'>'}</span>
                  <NavLink to={`/products/${product.tag}`} style={{ margin: '0 8px', textDecoration: 'none', color: '#6b7280', textTransform: 'capitalize' }}>
                    {product.tag}
                  </NavLink>
                </>
              ) : null}

              <span aria-hidden>{'>'}</span>
              <span style={{ marginLeft: 8 }}>{product.name}</span>

          </div>

        </nav>



        {error && (

          <div style={{ marginBottom: 16, color: '#dc2626', fontSize: 13 }}>

            {error}

          </div>

        )}



        <div

          style={{

            display: 'grid',

            gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1.1fr) minmax(0, 1.1fr)',

            gap: isMobile ? 20 : 28,

            alignItems: 'flex-start',

          }}

        >

          <section aria-label="Product media">

            <div
              style={{
                borderRadius: 12,
                overflow: 'hidden',
                background: cardBg,
                border: '1px solid #e5e7eb',
                marginBottom: 16
              }}
            >
              <div
                style={{
                  aspectRatio: '4/3',
                  width: '100%',
                  backgroundImage: mainImage ? `url(${mainImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: '#e5e7eb',
                  transition: 'background-image 0.3s ease-in-out'
                }}
              />
            </div>
            
            {galleryImages.length > 1 && (
              <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    style={{
                      border: activeImageIndex === idx ? '2px solid #22c55e' : '1px solid #e5e7eb',
                      borderRadius: 8,
                      width: 80,
                      height: 80,
                      minWidth: 80,
                      padding: 0,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      background: 'transparent'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${img})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </section>



          <section aria-label="Product information">

            <h1 style={{ margin: '0 0 8px', fontSize: isMobile ? 22 : 28 }}>{product.name}</h1>

            {product.brand && (

              <p style={{ margin: '0 0 6px', fontSize: 12, color: '#6b7280' }}>

                Brand: {product.brand}

              </p>

            )}

            <p

              style={{

                margin: '0 0 10px',

                fontSize: 12,

                letterSpacing: 0.5,

                textTransform: 'uppercase',

                color: product.inStock === false ? '#dc2626' : '#22c55e',

              }}

            >

              {product.inStock === false ? 'Out of stock' : 'In stock'}

            </p>

            <p

              style={{

                margin: '0 0 16px',

                fontSize: isMobile ? 13 : 14,

                lineHeight: '22px',

                color: isNightMode ? '#cbd5e1' : '#4b5563',

              }}

            >

              {description}

            </p>



            <div style={{ marginBottom: isMobile ? 16 : 20 }}>

              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>Approx. price</div>

              <div style={{ fontSize: 24, fontWeight: 600, color: '#16a34a' }}>

                {showProductRate ? formatCurrency(product.price) : 'Contact for Price'}

              </div>

              {showProductRate && (

                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>

                  Per unit (prices may vary by location)

                </div>

              )}

            </div>



            <div

              style={{

                display: 'flex',

                flexDirection: isMobile ? 'column' : 'row',

                gap: 10,

                marginBottom: 24,

              }}

            >

              <button

                type="button"

                onClick={() => setShowEnquiryModal(true)}

                style={{

                  padding: '10px 18px',

                  borderRadius: 9999,

                  border: 'none',

                  background: '#22c55e',

                  color: '#ffffff',

                  fontWeight: 600,

                  cursor: 'pointer',

                  fontSize: 14,

                  width: isMobile ? '100%' : 'auto',

                }}

              >

                Make an Enquiry

              </button>

            </div>



            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 4 }}>

              <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>Specifications</h2>

              <ul

                style={{

                  paddingLeft: 18,

                  margin: 0,

                  fontSize: 13,

                  lineHeight: '22px',

                  color: isNightMode ? '#cbd5e1' : '#4b5563',

                }}

              >

                {specifications.map((item) => (

                  <li key={item}>{item}</li>

                ))}

              </ul>

            </div>

          </section>

        </div>



        {relatedProducts.length > 0 && (

          <section style={{ marginTop: 40 }}>

            <h2 style={{ fontSize: 22, margin: '32px 0 16px' }}>Explore More Products</h2>

            <div

              style={{

                display: 'grid',

                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',

                gap: 16,

              }}

            >

              {relatedProducts.map((item) => (

                <article

                  key={item._id}

                  role="button"

                  tabIndex={0}

                  onClick={() => navigate(`/product/${item._id}`)}

                  onKeyDown={(event) => {

                    if (event.key === 'Enter' || event.key === ' ') {

                      event.preventDefault();

                      navigate(`/product/${item._id}`);

                    }

                  }}

                  style={{

                    borderRadius: 12,

                    overflow: 'hidden',

                    background: cardBg,

                    border: '1px solid #e5e7eb',

                    display: 'flex',

                    flexDirection: 'column',

                    cursor: 'pointer',

                  }}

                >

                  <div

                    style={{

                      aspectRatio: '4/3',

                      backgroundImage: item.imageUrl ? `url(${item.imageUrl})` : 'none',

                      backgroundSize: 'cover',

                      backgroundPosition: 'center',

                      backgroundColor: '#e5e7eb',

                    }}

                  />

                  <div style={{ padding: 14, display: 'flex', flexDirection: 'column', flex: 1, gap: 8 }}>

                    <h3 style={{ margin: 0, fontSize: 16 }}>{item.name}</h3>

                    <div style={{ fontSize: 14, fontWeight: 600, color: '#16a34a', minHeight: 24 }}>

                      {showProductRate ? formatCurrency(item.price) : 'Contact for Price'}

                    </div>

                    <div style={{ marginTop: 'auto', paddingTop: 8, width: '100%' }}>

                      <NavLink

                        to={`/product/${item._id}`}

                        onClick={(event) => event.stopPropagation()}

                        style={{

                          width: '100%',

                          padding: '9px 12px',

                          borderRadius: 8,

                          border: '1px solid #16a34a',

                          background: 'transparent',

                          color: '#16a34a',

                          textDecoration: 'none',

                          display: 'flex',

                          alignItems: 'center',

                          justifyContent: 'center',

                          textAlign: 'center',

                          fontSize: 13,

                          fontWeight: 600,

                          boxSizing: 'border-box',

                          transition: 'all 0.2s ease',

                        }}

                      >

                        {showProductRate ? 'View Product' : 'Get Best Price'}

                      </NavLink>

                    </div>

                  </div>

                </article>

              ))}

            </div>

          </section>

        )}

      </div>

      {/* Enquiry Modal */}
      {showEnquiryModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowEnquiryModal(false)}
        >
          <div
            style={{
              backgroundColor: isNightMode ? '#1e293b' : '#fff',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              position: 'relative',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowEnquiryModal(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: isNightMode ? '#cbd5e1' : '#666'
              }}
            >
              ×
            </button>

            {/* Modal Header */}
            <h2 style={{ 
              marginBottom: '20px', 
              color: isNightMode ? '#f1f5f9' : '#1a1a1a',
              fontSize: '20px',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              Contact Us
            </h2>

            {/* Status Message */}
            {enquiryStatus && (
              <div style={{
                padding: '10px 12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px',
                textAlign: 'center',
                fontWeight: '500',
                background: enquiryStatus.includes('successfully') ? '#d4edda' : 
                           enquiryStatus.includes('Submitting') ? '#d1ecf1' : '#f8d7da',
                color: enquiryStatus.includes('successfully') ? '#155724' : 
                       enquiryStatus.includes('Submitting') ? '#0c5460' : '#721c24',
                border: `1px solid ${
                  enquiryStatus.includes('successfully') ? '#c3e6cb' : 
                  enquiryStatus.includes('Submitting') ? '#bee5eb' : '#f5c6cb'
                }`
              }}>
                {enquiryStatus}
              </div>
            )}

            {/* Form Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px',
                  color: isNightMode ? '#cbd5e1' : '#333',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={enquiryData.name}
                  onChange={(e) => setEnquiryData({...enquiryData, name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${isNightMode ? '#475569' : '#ddd'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isNightMode ? '#334155' : '#fff',
                    color: isNightMode ? '#f1f5f9' : '#333'
                  }}
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px',
                  color: isNightMode ? '#cbd5e1' : '#333',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={enquiryData.email}
                  onChange={(e) => setEnquiryData({...enquiryData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${isNightMode ? '#475569' : '#ddd'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isNightMode ? '#334155' : '#fff',
                    color: isNightMode ? '#f1f5f9' : '#333'
                  }}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px',
                  color: isNightMode ? '#cbd5e1' : '#333',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={enquiryData.phone}
                  onChange={(e) => setEnquiryData({...enquiryData, phone: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${isNightMode ? '#475569' : '#ddd'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isNightMode ? '#334155' : '#fff',
                    color: isNightMode ? '#f1f5f9' : '#333'
                  }}
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px',
                  color: isNightMode ? '#cbd5e1' : '#333',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Message *
                </label>
                <textarea
                  value={enquiryData.message}
                  onChange={(e) => setEnquiryData({...enquiryData, message: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${isNightMode ? '#475569' : '#ddd'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isNightMode ? '#334155' : '#fff',
                    color: isNightMode ? '#f1f5f9' : '#333',
                    minHeight: '100px',
                    resize: 'vertical'
                  }}
                  placeholder="Tell us about your requirements..."
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="button"
                className="no-popup-submit"
                onClick={handleSubmitEnquiry}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#16a34a',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#15803d'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#16a34a'}
              >
                Submit Enquiry
              </button>
            </div>
          </div>
        </div>
      )}

    </main>

  );

}

